import { PaginationConfig } from '@/components/features/data/DataTable'
import { ROW_ACTION_BTN_CLASS } from '@/components/features/data/DataTable/RowActionButton'
import Settings from '@/components/features/data/DataTable/Settings'
import FlyoutMenu from '@/components/layout/FlyoutMenu'
import {
  ASSET_SET_TYPE_LABEL,
  ASSET_SET_TYPE_TONE,
} from '@/lib/assetSetDisplay'
import { captureDisplayDate } from '@/lib/captureTime'
import { userDisplayName, userFor, userInitials } from '@/lib/userDisplay'
import { cn, formatBytes } from '@/lib/utils'
import {
  ArrowDownTrayIcon,
  EyeIcon,
  LinkIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/20/solid'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'

import { AssetSet } from '@droidmap/asset-service-contract'
import { Avatar } from '@droidmap/frontend-ui'
import { Pagination, getPaginationSummary } from '@droidmap/frontend-ui'
import { Badge } from '@droidmap/frontend-ui'
import { MenuItem } from '@droidmap/frontend-ui'
import { AssetSetType, StorageSummary } from '@droidmap/shared-service-contract'
import { User } from '@droidmap/user-service-contract'

const totalStorageBytes = (s: StorageSummary | undefined): number => {
  if (!s) return 0
  return (Object.values(s) as unknown[]).reduce<number>(
    (sum, v) => sum + (typeof v === 'number' ? v : 0),
    0,
  )
}

function TypeTag({ type }: { type: AssetSetType }) {
  return (
    <Badge tone={ASSET_SET_TYPE_TONE[type]}>{ASSET_SET_TYPE_LABEL[type]}</Badge>
  )
}

function SizeCell({ bytes, max }: { bytes: number; max: number }) {
  const pct = max > 0 ? Math.round((bytes / max) * 100) : 0
  return (
    <div className="flex items-center justify-end gap-3">
      <div className="bg-border h-[3px] w-[54px] overflow-hidden rounded-[2px]">
        <div
          className="from-accent-2 to-accent h-full rounded-[2px] bg-gradient-to-r"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-text font-mono text-[13px] tabular-nums">
        {formatBytes(bytes, 2)}
      </span>
    </div>
  )
}

export type AssetSetSortKey =
  'name' | 'assets' | 'size' | 'created' | 'capturedAt'
export type SortDirection = 'asc' | 'desc'

/**
 * Format an asset set's capture date (the latest EXIF capture date across its
 * images) for display, or an em dash when no contained image has a capture
 * date.
 *
 * Shown in the *photographer's* clock, not the viewer's: `capturedAt` is a
 * true instant, so localising it would move a flight flown early or late in
 * its own day onto the neighbouring date for every viewer in another timezone
 * — and the same set would then read differently for two people looking at the
 * same table.
 */
const formatCapturedAt = (assetSet: AssetSet): string => {
  const d = captureDisplayDate(
    assetSet.capturedAt,
    assetSet.capturedAtOffsetMinutes,
  )
  if (!d) return '—'
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export interface AssetSetSort {
  key: AssetSetSortKey
  dir: SortDirection
}

interface ColumnDef {
  key?: AssetSetSortKey
  label: string
  align: 'left' | 'right'
}

const COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Name', align: 'left' },
  { label: 'Type', align: 'left' },
  { key: 'assets', label: 'Assets', align: 'right' },
  { key: 'size', label: 'Size', align: 'right' },
  { key: 'created', label: 'Created', align: 'left' },
  { key: 'capturedAt', label: 'Captured', align: 'left' },
  { label: 'Owner', align: 'left' },
  { label: 'Actions', align: 'right' },
]

export interface AssetSetTableProps {
  assetSets: AssetSet[]
  userMap: Record<string, User>
  onView: (assetSet: AssetSet) => void
  onDownload: (assetSet: AssetSet) => void
  onDelete: (assetSet: AssetSet) => void
  /** Row Edit action. When provided, the kebab menu shows Edit. */
  onEdit?: (assetSet: AssetSet) => void
  /** Row Share action. When provided, the kebab menu shows Share. */
  onShare?: (assetSet: AssetSet) => void
  /** Active sort + onSort handler. Headers without a `key` aren't clickable. */
  sort?: AssetSetSort
  onSortChange?: (sort: AssetSetSort) => void
  /**
   * Server-side pagination config, from either `useOffsetPaginationQuery` or
   * `useCursorPaginationQuery`. The `hasMoreBeyondKnownPages` flag on it tells
   * the footer which of the two it is; this component just forwards it.
   */
  pagination?: PaginationConfig
}

export function AssetSetTable({
  assetSets,
  userMap,
  onView,
  onDownload,
  onDelete,
  onEdit,
  onShare,
  sort,
  onSortChange,
  pagination,
}: AssetSetTableProps) {
  const maxBytes = assetSets.reduce(
    (m, a) => Math.max(m, totalStorageBytes(a.storageSummary)),
    0,
  )

  const handleSort = (key: AssetSetSortKey) => {
    if (!onSortChange) return
    if (sort?.key === key) {
      onSortChange({ key, dir: sort.dir === 'asc' ? 'desc' : 'asc' })
    } else {
      // Default direction: descending for numeric/date columns, ascending for name.
      onSortChange({ key, dir: key === 'name' ? 'asc' : 'desc' })
    }
  }

  return (
    <div className="border-border bg-panel mb-3.5 overflow-hidden rounded-lg border">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {COLUMNS.map((col) => {
                const isSortable = col.key !== undefined
                const isActive = sort?.key === col.key
                return (
                  <th
                    key={col.label}
                    className={cn(
                      'border-border bg-bg-2 text-text-mute whitespace-nowrap border-b px-5 py-3 font-mono text-[10px] font-medium uppercase tracking-[0.16em]',
                      col.align === 'right' ? 'text-right' : 'text-left',
                      isSortable &&
                        'hover:text-text-dim cursor-pointer select-none',
                      isSortable && isActive && 'text-accent',
                    )}
                    onClick={() => col.key && handleSort(col.key)}
                  >
                    {col.label}
                    {isSortable && (
                      <span
                        className={cn(
                          'ml-1.5 inline-flex align-middle',
                          !isActive && 'opacity-40',
                        )}
                      >
                        {isActive && sort?.dir === 'asc' ? (
                          <ChevronUpIcon className="h-2.5 w-2.5" />
                        ) : (
                          <ChevronDownIcon className="h-2.5 w-2.5" />
                        )}
                      </span>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {assetSets.map((assetSet) => {
              const owner = userFor(userMap, assetSet.createdBy)
              const ownerName = userDisplayName(owner)
              const ownerInitials = userInitials(owner)
              const size = totalStorageBytes(assetSet.storageSummary)
              const created = assetSet.created
                ? new Date(assetSet.created).toLocaleDateString(undefined, {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : '—'
              return (
                <tr
                  key={assetSet.id}
                  className={cn(
                    'border-border cursor-pointer border-b last:border-b-0 transition-colors duration-150',
                    'hover:bg-accent/[0.028]',
                  )}
                  onClick={() => onView(assetSet)}
                >
                  <td className="text-text px-5 py-3.5 align-middle text-[13px] font-medium">
                    <span className="truncate">{assetSet.name}</span>
                    <span className="text-text-mute mt-0.5 block font-mono text-[10px] tracking-wide">
                      ID · {assetSet.id}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 align-middle">
                    <TypeTag type={assetSet.assetSetType} />
                  </td>
                  <td className="text-text px-5 py-3.5 text-right align-middle font-mono text-[13px] tabular-nums">
                    {assetSet.assetCount ?? 0}
                  </td>
                  <td className="px-5 py-3.5 align-middle">
                    <SizeCell bytes={size} max={maxBytes} />
                  </td>
                  <td className="text-text-dim whitespace-nowrap px-5 py-3.5 align-middle font-mono text-[12px]">
                    {created}
                  </td>
                  <td className="text-text-dim whitespace-nowrap px-5 py-3.5 align-middle font-mono text-[12px]">
                    {formatCapturedAt(assetSet)}
                  </td>
                  <td className="px-5 py-3.5 align-middle">
                    <div className="text-text flex items-center gap-2.5 text-[13px]">
                      <Avatar
                        size="md"
                        color="violet"
                        initials={ownerInitials}
                      />
                      <span className="truncate">{ownerName}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-right align-middle">
                    <div
                      className="inline-flex justify-end"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FlyoutMenu buttonClassName={ROW_ACTION_BTN_CLASS}>
                        {onEdit && (
                          <MenuItem
                            icon={<PencilIcon className="w-4 h-4" />}
                            onClick={() => onEdit(assetSet)}
                          >
                            Edit
                          </MenuItem>
                        )}
                        <MenuItem
                          icon={<EyeIcon className="w-4 h-4" />}
                          onClick={() => onView(assetSet)}
                        >
                          View
                        </MenuItem>
                        <MenuItem
                          icon={<ArrowDownTrayIcon className="w-4 h-4" />}
                          onClick={() => onDownload(assetSet)}
                        >
                          Download
                        </MenuItem>
                        {onShare && (
                          <MenuItem
                            icon={<LinkIcon className="w-4 h-4" />}
                            onClick={() => onShare(assetSet)}
                          >
                            Share
                          </MenuItem>
                        )}
                        <MenuItem
                          icon={<TrashIcon className="w-4 h-4" />}
                          tone="danger"
                          onClick={() => onDelete(assetSet)}
                        >
                          Delete
                        </MenuItem>
                      </FlyoutMenu>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {pagination && assetSets.length > 0 && (
        <div className="border-border text-text-dim flex items-center justify-between gap-3 border-t px-5 py-3.5 font-mono text-[12px] uppercase tracking-[0.06em]">
          <span>
            {getPaginationSummary({
              startRow:
                pagination.state.pageIndex * pagination.state.pageSize + 1,
              endRow:
                pagination.state.pageIndex * pagination.state.pageSize +
                assetSets.length,
              total: pagination.totalCount,
              hasMoreBeyondKnownPages: pagination.hasMoreBeyondKnownPages,
            })}
          </span>
          <div className="flex items-center gap-2">
            <Settings
              pageSize={pagination.state.pageSize}
              onPageSizeChange={(pageSize) =>
                pagination.onChange({ pageIndex: 0, pageSize })
              }
            />
            <Pagination
              currentPage={pagination.state.pageIndex + 1}
              maxPage={pagination.pageCount ?? pagination.state.pageIndex + 1}
              hasMoreBeyondKnownPages={
                pagination.hasMoreBeyondKnownPages ?? false
              }
              onPageChange={(page) =>
                pagination.onChange({
                  ...pagination.state,
                  pageIndex: page - 1,
                })
              }
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default AssetSetTable
